import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";

/**
 * Pull-to-refresh wrapper for the admin side of the app.
 * - Engages only when the document is scrolled to the very top.
 * - Visual indicator follows the finger, then triggers window.location.reload()
 *   once the pull exceeds the threshold.
 */
const THRESHOLD = 80;
const MAX_PULL = 140;

export default function PullToRefreshAdmin({ children }) {
  const startYRef = useRef(null);
  const activeRef = useRef(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const onTouchStart = (e) => {
      if (refreshing) return;
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      if (scrollY > 0) return;
      startYRef.current = e.touches[0].clientY;
      activeRef.current = false;
    };
    const onTouchMove = (e) => {
      if (startYRef.current == null) return;
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy > 0) {
        activeRef.current = true;
        // dampen so the pull feels rubbery
        const damped = Math.min(MAX_PULL, dy * 0.5);
        setPull(damped);
      } else if (dy < 0) {
        // Cancel if user scrolls back up
        startYRef.current = null;
        setPull(0);
      }
    };
    const onTouchEnd = () => {
      const dist = pull;
      startYRef.current = null;
      if (activeRef.current && dist >= THRESHOLD) {
        setRefreshing(true);
        setPull(THRESHOLD);
        // Slight delay so the indicator paints before reload
        window.setTimeout(() => window.location.reload(), 220);
      } else {
        setPull(0);
      }
      activeRef.current = false;
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd);
    document.addEventListener("touchcancel", onTouchEnd);
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
    // pull is read in onTouchEnd via closure, but state changes don't need re-binding
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing]);

  const ready = pull >= THRESHOLD;

  return (
    <>
      <div
        aria-hidden={pull === 0 && !refreshing}
        className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none"
        style={{
          transform: `translateY(${Math.max(0, pull - 40)}px)`,
          opacity: pull > 8 || refreshing ? 1 : 0,
          transition: pull === 0 ? "transform 200ms ease-out, opacity 200ms ease-out" : "none",
        }}
        data-testid="pull-to-refresh-indicator"
      >
        <div className="mt-2 px-3 py-1.5 rounded-full bg-zinc-900 text-white text-xs font-semibold tracking-wide shadow-md inline-flex items-center gap-2">
          <RefreshCw
            className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
            style={{
              transform: !refreshing ? `rotate(${Math.min(180, pull * 2.2)}deg)` : undefined,
              transition: refreshing ? "none" : "transform 60ms linear",
            }}
          />
          {refreshing ? "Refreshing…" : ready ? "Release to refresh" : "Pull to refresh"}
        </div>
      </div>
      {children}
    </>
  );
}
